<template>
  <div>
    <TopBar />

    <main class="m-auto px-6" style="max-width: 1190px;">
      <div class="lg:flex lg:items-start">
        <Sidebar />

        <div class="mt-16 w-full">
          <h1 id="our-story" class="mb-5 text-3xl font-bold text-slate xl:text-4xl">
            Our Story
          </h1>
          <p class="text-xl text-gray-dark">
            Tripexpert was conceived as a better, smarter alternative to user-review travel sites. We feature professional hotel, restaurant and attraction reviews from trusted sources — succinct information that helps you make decisions on where to stay, dine and go with confidence.
          </p>

          <div class="my-10" />

          <h1 id="tripexpert-score" class="mb-5 text-3xl font-bold text-slate xl:text-4xl">
            The Tripexpert Score
          </h1>
          <p class="mb-5 text-xl text-gray-dark">
            We distill expert advice from 100+ publications.
          </p>

          <div class="mb-8 flex flex-wrap items-center md:flex-nowrap">
            <div class="mb-5 mr-0 w-full text-center md:mb-0 md:mr-5 md:w-auto xl:flex-none">
              <img loading="lazy" class="inline-block max-h-full w-80 max-w-full" src="~assets/images/about/pub1.svg" alt="">
            </div>
            <div class="w-full">
              <h2 class="mb-2 text-2xl font-semibold leading-tight text-gray-dark">
                Now featuring over 1 million expert reviews.
              </h2>
              <p class="text-xl text-gray-dark">
                Our sources include travel guides, magazines and newspapers. We also include reviews from respected food and travel bloggers and other local experts.
              </p>
            </div>
          </div>

          <div class="mb-8 flex flex-wrap items-center md:flex-nowrap">
            <div class="mb-5 mr-0 w-full text-center md:mb-0 md:mr-5 md:w-auto xl:flex-none">
              <img loading="lazy" class="inline-block max-h-full w-80 max-w-full" src="~assets/images/about/pub2.svg" alt="">
            </div>

            <div class="w-full">
              <h2 class="mb-2 text-2xl font-semibold leading-tight text-gray-dark">
                The most important information at a glance
              </h2>
              <p class="text-xl text-gray-dark">
                We read every review and select a snippet that best summarizes it. We call out advice, like what room to ask for or what dishes you can’t miss.
              </p>
            </div>
          </div>

          <div class="mb-8 flex flex-wrap items-center md:flex-nowrap">
            <div class="mb-5 mr-0 w-full text-center md:mb-0 md:mr-5 md:w-auto xl:flex-none">
              <img loading="lazy" class="inline-block max-h-full w-80 max-w-full" src="~assets/images/about/pub3.svg" alt="">
            </div>

            <div class="w-full">
              <h2 class="mb-2 text-2xl font-semibold leading-tight text-gray-dark">
                The only score you need to know.
              </h2>
              <p class="text-xl text-gray-dark">
                The final result of our review collection and curation process is the Tripexpert Score. Only the best venues — those that have been recommended by multiple experts — receive a score.
              </p>
            </div>
          </div>

          <h1 id="best-price-guarantee" class="mb-6 text-3xl font-bold text-slate xl:text-4xl">
            Best price guarantee
          </h1>
          <p class="mb-3 text-xl text-gray-dark">
            We’ve partnered with Booking.com to provide you with the best rates on hotel reservations. If you find a lower price for the same room on the same dates, we'll refund the difference.*
          </p>
          <p class="mb-16 text-sm text-body">
            * This offer applies only to rates available to the general public. It does not include blind bookings, or rates available only through hotel loyalty programs.
          </p>

          <h1 id="media-coverage" class="text-3xl font-bold text-slate xl:text-4xl">
            Media coverage
          </h1>
          <div class="my-4 flex flex-wrap items-center">
            <div class="mr-8 py-4 xl:mr-16">
              <a href="https://www.nytimes.com/2014/10/05/travel/finding-your-travel-tribe.html">
                <img loading="lazy" width="228" src="~/assets/images/media-coverage-img-1.png">
              </a>
            </div>
            <div class="mr-8 py-4 xl:mr-16">
              <a href="http://scitechnow.org/videos/emily-hughes-explains-how-tripexpert-aims-to-be-a-definitive-statement-of-the-overall-quality-based-on-professional-reviews">
                <img loading="lazy" width="101" src="~/assets/images/media-coverage-img-2.png">
              </a>
            </div>
            <div class="mr-8 py-4 xl:mr-16">
              <a href="http://www.latimes.com/travel/deals/la-tr-webbuzz-20140713-story.html">
                <img loading="lazy" width="249" src="~/assets/images/media-coverage-img-3.png">
              </a>
            </div>
            <div class="py-4">
              <a href="https://static.tripexpert.com/press/gq.pdf">
                <img loading="lazy" width="76" src="~/assets/images/media-coverage-img-4.png">
              </a>
            </div>
          </div>
          <p class="mb-8 text-sm text-body">
            And also:
            <a class="text-blue hover:underline" href="https://www.bangkokpost.com/business/news/1619186/kingsmen-talks-the-importance-of-service">Bangkok Post</a>,
            <a class="text-blue hover:underline" href="http://bostinno.streetwise.co/2015/01/26/tripadvisor-alternative-tripexpert-hotel-ratings-ignore-user-reviews/">BostInno</a>,
            <a class="text-blue hover:underline" href="http://www.businessinsider.com/cool-travel-websites-2014-9">Business Insider</a>,
            <a class="text-blue hover:underline" href="https://www.clarin.com/ciudades/buenos-aires-preferida-turistas-ciudades-sudamericanas_0_Skon0p2wz.html">Clarín (Argentina)</a>,
            <a class="text-blue hover:underline" href="https://www.cnbc.com/id/102769571">CNBC</a>,
            <a class="text-blue hover:underline" href="https://www.zeit.de/2014/33/byte-gereist-hotelbewertung">Die Zeit (Germany)</a>,
            <a class="text-blue hover:underline" href="http://www.entrepreneur.com/article/249562">Entrepreneur</a>,
            <a class="text-blue hover:underline" href="http://smallbusiness.foxbusiness.com/entrepreneurs/2014/07/08/4-ways-to-make-your-business-trip-more-productive/">Fox Business</a>,
            <a class="text-blue hover:underline" href="http://www.hotelchatter.com/story/2014/6/9/202540/3587/hotels/How_TripExpert,_a_New_Review_Site,_is_Doing_Things_Differently_Than_TripAdvisor">HotelChatter</a>,
            <a class="text-blue hover:underline" href="https://www.huffingtonpost.co.uk/entry/10-things-to-see-and-do-in-copenhagen-instagram-inspo-for-your-denmark-city-break_uk_5bf6aa82e4b03b230f9f3505">Huffington Post</a>,
            <a class="text-blue hover:underline" href="http://skift.com/2014/09/29/5-new-travel-startups-that-crowdsource-travel-tips/">Skift</a>,
            <a class="text-blue hover:underline" href="http://www.azcentral.com/story/nowdeparting/2014/07/01/trip-expert-offers-hotel-reviews-from-travel-pros/11926439/">The Arizona Republic</a>,
            <a class="text-blue hover:underline" href="https://www.telegraph.co.uk/travel/hotels/11436367/Poor-service-to-blame-for-half-of-bad-TripAdvisor-reviews.html">The Daily Telegraph</a>,
            <a class="text-blue hover:underline" href="http://www.seattletimes.com/life/travel//how-to-find-online-travel-reviews-suited-to-you/">The Seattle Times</a>,
            <a class="text-blue hover:underline" href="https://time.com/money/3926582/hotel-discount-disappears/">Time</a>,
            <a class="text-blue hover:underline" href="http://www.tnooz.com/article/startup-pitch-TripExpert-aims-to-be-the-most-trustable-hotel-review-platform/">Tnooz</a>,
            <a class="text-blue hover:underline" href="http://thenextweb.com/events/2015/11/11/16-new-york-startups-you-need-to-check-out/">TNW</a>,
            <a class="text-blue hover:underline" href="https://www.usatoday.com/story/travel/hotels/2014/08/31/tripexpert-best-business-travel-hotels/14586171/">USA Today</a>,
            <a class="text-blue hover:underline" href="http://roadwarriorvoices.com/2015/02/07/3-travel-review-alternatives-to-tripadvisor/">USA Today Travel</a>
          </p>
          <p class="text-xl text-gray-dark">
            Members of the press can download our <a href="https://static.tripexpert.com/resources/press-kit.zip" class="text-blue hover:underline">Press Kit</a>.
          </p>
        </div>
      </div>
    </main>

    <MainFooter />
  </div>
</template>

<script>
import TopBar from '~/components/layout/TopBar'
import MainFooter from '~/components/layout/MainFooter'
import Sidebar from '~/components/layout/Sidebar'
import { useHead } from '#imports'

export default {
  components: { TopBar, Sidebar, MainFooter },

  setup () {
    useHead({
      title: 'Tripexpert - About',
      meta: [
        { hid: 'description', name: 'description', content: 'Tripexpert - About' },
        { hid: 'og:description', property: 'og:description', content: 'Tripexpert - About' },
      ],
    })
  },
}
</script>
